import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { CurrentUser } from '../api/types'

type AuthState = {
  user: CurrentUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => api<{ user: CurrentUser | null }>('/auth/me'),
    // The session is the source of truth; don't serve a stale identity.
    staleTime: 0,
  })

  const signInMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<{ user: CurrentUser }>('/auth/login', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries(),
  })

  const signOutMutation = useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      // Drop every cached query so nothing from the previous user lingers.
      queryClient.clear()
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api<{ ok: true }>('/auth/change-password', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries(),
  })

  const signIn = useCallback(
    async (email: string, password: string) => {
      await signInMutation.mutateAsync({ email, password })
    },
    [signInMutation],
  )

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync()
  }, [signOutMutation])

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await changePasswordMutation.mutateAsync({ currentPassword, newPassword })
    },
    [changePasswordMutation],
  )

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['me'] })
  }, [queryClient])

  const value = useMemo<AuthState>(
    () => ({
      user: data?.user ?? null,
      loading: isLoading,
      signIn,
      signOut,
      changePassword,
      refresh,
    }),
    [data?.user, isLoading, signIn, signOut, changePassword, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
